// ============================================================
// SHARED UI STYLES — edit here to realign all pages at once.
//
// All structural styles (header, strips, buttons, inputs, etc.)
// are defined here. Data-semantic styles (green/red for values,
// per-chart colours) stay in each page file.
// ============================================================
import { C, F, MONO, SANS } from './theme';

// ── Spacing scale (px) ──────────────────────────────────────
export const SP = {
  xs:   3,   // tight padding inside tags/small buttons
  sm:   6,   // default sibling gap
  md:   8,   // strip top/bottom padding
  lg:  12,
  xl:  16,
  xxl: 20,   // page-level horizontal padding
};

// ── Border-radius scale ──────────────────────────────────────
export const RADIUS = {
  sm: 3,   // buttons, inputs, chips
  md: 4,   // header badges, DATA button, ds chips
  lg: 6,   // tooltips, logo icon, larger cards
};

// ── Tab-bar height — must match App.js header height ─────────
export const HEADER_H = 52;

// ── Right-panel width — shared by all page layouts ───────────
export const PANEL_W = 380;

// ── Static style objects ─────────────────────────────────────
export const S = {

  // Page root
  page: {
    background: C.bg,
    color: C.tx,
    minHeight: '100vh',
    fontFamily: SANS,
    fontSize: F.md,
  },

  // Standard two-column layout (left + fixed right panel)
  pageContent: {
    display: 'grid',
    gridTemplateColumns: `1fr ${PANEL_W}px`,
    minHeight: `calc(100vh - ${HEADER_H}px)`,
  },

  // Full-height flex layout (used by pages without their own header)
  pageContentFull: {
    display: 'flex',
    height: `calc(100vh - ${HEADER_H}px)`,
  },

  // Left scrollable column
  leftCol: {
    borderRight: `1px solid ${C.bd}`,
    display: 'flex',
    flexDirection: 'column',
  },

  // Right detail panel
  rightPanel: {
    background: C.bgC,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  // ── Page header bar ──────────────────────────────────────
  header: {
    background: C.bgC,
    borderBottom: `1px solid ${C.bd}`,
    padding: `${SP.md}px ${SP.xxl}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  headerLeft:  { display: 'flex', alignItems: 'center', gap: 10 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16 },

  pageTitle: { fontWeight: 700, fontSize: F.h2, letterSpacing: '-.02em' },
  pageSub:   { fontSize: F.xxs, color: C.txD, letterSpacing: '.1em', textTransform: 'uppercase' },

  spotLabel: { fontSize: F.xxs, color: C.txD, textTransform: 'uppercase' },
  asOf:      { fontSize: F.xs,  color: C.txM },

  // DATA toggle button in header
  dataBtn: {
    background: 'transparent',
    border: `1px solid ${C.bd}`,
    borderRadius: RADIUS.md,
    padding: '2px 8px',
    fontSize: F.xs,
    color: C.txD,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 3,
  },

  // Data-source info strip (below header, toggled by DATA button)
  dsRow: {
    background: C.bgC,
    borderBottom: `1px solid ${C.bd}`,
    padding: `6px ${SP.xxl}px`,
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },

  dsChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    borderRadius: RADIUS.md,
    border: `1px solid ${C.bd}`,
    fontSize: F.xs,
  },

  // ── Horizontal control strips ─────────────────────────────
  // Standard strip (chart controls, view mode, rank, etc.)
  strip: {
    padding: `${SP.md}px 14px`,
    borderBottom: `1px solid ${C.bd}`,
    display: 'flex',
    gap: SP.sm,
    alignItems: 'center',
    flexWrap: 'wrap',
  },

  // Primary strip (tenor/expiry selectors, slightly raised)
  stripPrimary: {
    padding: `${SP.md}px 14px`,
    borderBottom: `1px solid ${C.bd}`,
    background: C.bgC,
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    flexWrap: 'wrap',
    flexShrink: 0,
  },

  // ── Labels & separators ───────────────────────────────────
  // ALL-CAPS section label
  label: {
    fontSize: F.xs,
    color: C.txD,
    textTransform: 'uppercase',
    letterSpacing: '.1em',
  },

  // Vertical rule between button groups
  divider: { width: 1, height: 16, background: C.bd, margin: '0 6px' },

  // ── Inputs ────────────────────────────────────────────────
  // Standard numeric/text input
  input: {
    background: C.bg,
    border: `1px solid ${C.bd}`,
    borderRadius: RADIUS.sm,
    padding: '4px 7px',
    color: C.tx,
    fontSize: F.lg,
    fontFamily: MONO,
    outline: 'none',
  },

  // Compact input (narrow strips, risk budget, etc.)
  inputSm: {
    background: C.bg,
    border: `1px solid ${C.bd}`,
    borderRadius: RADIUS.sm,
    padding: '3px 5px',
    color: C.tx,
    fontSize: F.md,
    fontFamily: MONO,
    outline: 'none',
  },

  // Select element
  select: {
    background: C.bg,
    color: C.tx,
    border: `1px solid ${C.bd}`,
    borderRadius: RADIUS.sm,
    padding: '3px 8px',
    fontSize: F.lg,
    fontFamily: MONO,
    outline: 'none',
    cursor: 'pointer',
  },

  // ── Base button (inactive / ghost) ────────────────────────
  btnGhost: {
    background: 'transparent',
    border: `1px solid ${C.bd}`,
    borderRadius: RADIUS.sm,
    padding: '3px 8px',
    fontSize: F.sm,
    fontWeight: 600,
    cursor: 'pointer',
    color: C.txD,
    fontFamily: MONO,
  },

  // ── Table primitives ──────────────────────────────────────
  th: {
    padding: '5px 10px',
    fontSize: F.xs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    color: C.txD,
    whiteSpace: 'nowrap',
  },

  td:    { padding: '5px 10px', fontSize: F.sm, fontFamily: MONO, color: C.txD },
  tdNum: { padding: '5px 10px', textAlign: 'right', fontFamily: MONO, fontSize: F.sm },

  // ── Recharts tooltip container ────────────────────────────
  tooltip: {
    background: C.bgC,
    border: `1px solid ${C.bd}`,
    borderRadius: RADIUS.lg,
    padding: '6px 10px',
    fontSize: F.sm,
    color: C.tx,
  },
};

// ── Factory functions (colour-dependent) ─────────────────────

// Toggle button: active → filled accent; inactive → ghost.
// Pass activeColor explicitly when the accent token doesn't have
// a matching *D (dim) sibling (e.g. custom MA colours).
export function btn(active, color = C.ac, activeColor) {
  if (!active) return S.btnGhost;
  return {
    background:   activeColor ?? `${color}20`,
    border:      `1px solid ${color}`,
    borderRadius: RADIUS.sm,
    padding:     '3px 8px',
    fontSize:     F.sm,
    fontWeight:   600,
    cursor:      'pointer',
    color,
    fontFamily:   MONO,
  };
}

// Coloured badge (view-summary, direction indicator, etc.)
export function badge(color) {
  return {
    padding:      '2px 8px',
    borderRadius:  RADIUS.md,
    fontSize:      F.sm,
    fontWeight:    700,
    background:   `${color}20`,
    color,
    border:       `1px solid ${color}40`,
  };
}

// Page-header logo icon
export function logoIcon(c1, c2) {
  return {
    width:          26,
    height:         26,
    borderRadius:   RADIUS.lg,
    background:    `linear-gradient(135deg, ${c1}, ${c2})`,
    display:       'flex',
    alignItems:    'center',
    justifyContent:'center',
    fontWeight:     700,
    fontSize:       F.xl,
    color:          C.bg,
  };
}

// Hero spot / rate number in page header
export function spotNum(color) {
  return { fontFamily: MONO, fontSize: F.d2, fontWeight: 600, color };
}
